import { RefObject, useEffect } from 'react';

/** What the trap cycles between. */
const FOCUSABLE_SELECTOR = 'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])';

/**
 * Keeps Tab inside a container while it is active, and hands focus back to whatever opened it.
 *
 * `aria-modal="true"` tells assistive technology that everything outside the node is unavailable.
 * Nothing enforces that on its own: the search pane is a DOM child of `<nav>`, so Tabbing past the
 * last result walked focus into the page behind a full-screen scrim — no visible focus ring, no way
 * to tell where focus had gone. On close, focus was dropped to `<body>` rather than returned to the
 * trigger, so the next Tab restarted from the top of the document.
 *
 * This is what makes the attribute true rather than a claim.
 */
export function useFocusTrap(containerRef: RefObject<HTMLElement>, isActive: boolean): void {
  useEffect(() => {
    if (!isActive) {
      return;
    }

    const restoreTo = document.activeElement as HTMLElement | null;
    const container = containerRef.current;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab' || !container) {
        return;
      }

      const focusable = [...container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)].filter(
        (element) => !element.hasAttribute('disabled')
      );
      if (focusable.length === 0) {
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      // Shift-Tab off the first element wraps to the last; Tab off the last wraps to the first.
      // `!container.contains(active)` also recovers focus that has already escaped.
      if (event.shiftKey && (active === first || !container.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      restoreTo?.focus?.();
    };
  }, [containerRef, isActive]);
}
